package dev.kuml.portal.web.editor

import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonClassDiscriminator

/**
 * Client-side mirror of `kuml-portal-server`'s `dev.kuml.portal.server.collab.ModelOp`/
 * `CollabWireProtocol`'s V4 frame kinds (R-27, kUML Portal "Interactive Diagram-Editing UI" wave) --
 * hand-mirrored, not a shared KMP module, same convention `CollabFrame.kt` already follows for the
 * server's `CollabWireProtocol.kt` (see that file's own class KDoc). Field names/discriminators/
 * validation limits below must be kept in lockstep with the server's `ModelOp.kt` by hand.
 *
 * ## What is deliberately NOT here (K-1)
 * [ClientAddAttribute]/[ClientUpdateAttribute] carry no multiplicity field -- see the server
 * `ModelOp`'s class KDoc "What is deliberately NOT here" for why: the interpreter never reads one
 * back, so offering it in the inspector panel would be a value that silently vanishes on the next
 * checkpoint. There is therefore no multiplicity input anywhere in this wave's attribute form.
 *
 * ## Client-side validation is a pre-check only, never authoritative
 * [ModelOpLimits] mirrors the server's own limits so the UI can reject an obviously-too-long name
 * before ever sending a frame (a same-tick, no-round-trip user experience) -- the server's
 * `DiagramLoroMutator` re-validates everything from scratch regardless, and its [ModelOpRejected]
 * response (via [decodeReject]) is the only outcome that actually matters.
 */
@OptIn(ExperimentalSerializationApi::class)
@Serializable
@JsonClassDiscriminator("op")
public sealed interface ClientModelOp

@Serializable
@SerialName("addClass")
public data class ClientAddClass(
    val name: String,
) : ClientModelOp

@Serializable
@SerialName("renameElement")
public data class ClientRenameElement(
    val elementId: String,
    val name: String,
) : ClientModelOp

@Serializable
@SerialName("deleteElement")
public data class ClientDeleteElement(
    val elementId: String,
) : ClientModelOp

@Serializable
@SerialName("setClassFlags")
public data class ClientSetClassFlags(
    val elementId: String,
    val isAbstract: Boolean? = null,
    val visibility: String? = null,
) : ClientModelOp

@Serializable
@SerialName("addAttribute")
public data class ClientAddAttribute(
    val classId: String,
    val name: String,
    val typeName: String,
    val visibility: String = "PRIVATE",
    val defaultValue: String? = null,
    val isStatic: Boolean = false,
    val isReadOnly: Boolean = false,
) : ClientModelOp

@Serializable
@SerialName("updateAttribute")
public data class ClientUpdateAttribute(
    val classId: String,
    val attrId: String,
    val name: String,
    val typeName: String,
    val visibility: String,
    val defaultValue: String? = null,
    val isStatic: Boolean = false,
    val isReadOnly: Boolean = false,
) : ClientModelOp

@Serializable
@SerialName("deleteAttribute")
public data class ClientDeleteAttribute(
    val classId: String,
    val attrId: String,
) : ClientModelOp

@Serializable
@SerialName("addOperation")
public data class ClientAddOperation(
    val classId: String,
    val name: String,
    val returnTypeName: String? = null,
    val visibility: String = "PUBLIC",
    val isAbstract: Boolean = false,
    val isStatic: Boolean = false,
) : ClientModelOp

@Serializable
@SerialName("updateOperation")
public data class ClientUpdateOperation(
    val classId: String,
    val opId: String,
    val name: String,
    val returnTypeName: String? = null,
    val visibility: String,
    val isAbstract: Boolean = false,
    val isStatic: Boolean = false,
) : ClientModelOp

@Serializable
@SerialName("deleteOperation")
public data class ClientDeleteOperation(
    val classId: String,
    val opId: String,
) : ClientModelOp

@Serializable
@SerialName("addAssociation")
public data class ClientAddAssociation(
    val sourceId: String,
    val targetId: String,
    val name: String? = null,
    val aggregation: String = "NONE",
) : ClientModelOp

@Serializable
@SerialName("updateAssociationEnd")
public data class ClientUpdateAssociationEnd(
    val assocId: String,
    val endIndex: Int,
    val role: String? = null,
    val multLower: Int = 1,
    val multUpper: Int? = 1,
    val navigable: Boolean = true,
) : ClientModelOp

/** Mirrors the server's `dev.kuml.portal.server.collab.ModelOpRejectReason` -- [code] matches the
 * server's wire byte exactly, so [ModelOpCodec.decodeReject] can resolve one from the raw frame. */
public enum class ClientModelOpRejectReason(
    public val code: Byte,
) {
    MALFORMED(1),
    UNKNOWN_ELEMENT(2),
    LIMIT_EXCEEDED(3),
    INVALID_VALUE(4),
    NO_LIVE_ROOM(5),
}

/** A decoded [dev.kuml.portal.web.editor.ModelOpCodec.decodeReject] result. */
public data class ModelOpReject(
    val reason: ClientModelOpRejectReason,
    val message: String,
)

/** Mirrors `kuml-portal-server`'s `dev.kuml.portal.server.collab.ModelOpLimits` -- a client-side
 * pre-check only (see this file's class KDoc), never authoritative. */
public object ModelOpLimits {
    public const val MAX_MODEL_OP_BYTES: Int = 4_096
    public const val MAX_NAME_CHARS: Int = 200
    public const val MAX_TYPE_CHARS: Int = 200
    public const val MAX_DEFAULT_VALUE_CHARS: Int = 500
}

/**
 * Encodes/decodes R-27's V4 wire frames (see [ClientModelOp]'s class KDoc). Mirrors
 * `kuml-portal-server`'s `CollabWireProtocol.kt` frame-kind bytes and payload shapes byte-for-byte:
 * `FRAME_KIND_MODEL_OP = 3` (client -> server, this connection's own outgoing frame, built by
 * [encode]), `FRAME_KIND_MODEL_REV = 4` (server -> client, `[8 bytes editVersion]`, decoded by
 * [decodeRevision]), `FRAME_KIND_MODEL_OP_REJECT = 5` (server -> client, `[1 byte reason][UTF-8
 * message]`, decoded by [decodeReject]).
 */
public object ModelOpCodec {
    private const val KIND_MODEL_OP: Byte = 3
    private const val KIND_MODEL_REV: Byte = 4
    private const val KIND_MODEL_OP_REJECT: Byte = 5

    private val json = Json { ignoreUnknownKeys = true }

    /** `[3][UTF-8 JSON of op]` -- the entire outgoing frame this connection sends for one committed
     * inspector-panel edit (see `CollabClient.kt`'s "sent only on commit, never per keystroke"
     * discipline this wire budget assumes). */
    public fun encode(op: ClientModelOp): ByteArray = byteArrayOf(KIND_MODEL_OP) + json.encodeToString(op).encodeToByteArray()

    /** `null` -- never throws -- for a wrong kind byte or a payload shorter than 9 bytes (1 kind +
     * 8 editVersion bytes). Big-endian, matching `CollabWireProtocol.encodeEditVersion`. */
    public fun decodeRevision(frame: ByteArray): Long? {
        if (frame.isEmpty() || frame[0] != KIND_MODEL_REV) return null
        if (frame.size != 9) return null
        var result = 0L
        for (i in 1..8) {
            result = (result shl 8) or (frame[i].toLong() and 0xFF)
        }
        return result
    }

    /** `null` -- never throws -- for a wrong kind byte, an empty payload (no reason byte at all), or
     * an unrecognised reason code. */
    public fun decodeReject(frame: ByteArray): ModelOpReject? {
        if (frame.isEmpty() || frame[0] != KIND_MODEL_OP_REJECT) return null
        if (frame.size < 2) return null
        val reason = ClientModelOpRejectReason.entries.find { it.code == frame[1] } ?: return null
        val message = frame.copyOfRange(2, frame.size).decodeToString()
        return ModelOpReject(reason, message)
    }

    /** `true` for any frame this codec recognises as belonging to the R-27 model-op protocol,
     * regardless of direction -- lets `CollabClient.kt`'s frame dispatch decide "is this even worth
     * trying [decodeRevision]/[decodeReject] on" without duplicating the kind-byte constants. */
    public fun isModelOpFrame(frame: ByteArray): Boolean =
        frame.isNotEmpty() && (frame[0] == KIND_MODEL_OP || frame[0] == KIND_MODEL_REV || frame[0] == KIND_MODEL_OP_REJECT)
}
